import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { moduleSpec } from '@yourstore/domain';
import { useAction, useApi } from '../../context';
import { Confirm, Failure } from '../../bits';
import { Chip, Menu, Panel, Switch, useToast } from '../../kit';
import type { MenuItem } from '../../kit';
import { useLocale, useT } from '../../../messages';
import type { ModuleRow, ModuleStatus } from '../../../wire';
import {
  FEATURE_GROUPS,
  FEATURE_ROWS,
  FOLDED_GROUPS,
  FOUNDATION,
  dependentsFor,
  missingFor,
  namesOf,
  rowGone,
  rowName,
  rowWhat,
} from './featureList';
import type { FeatureGroup, FeatureRow } from './featureList';

/**
 * **Features** — what the business has in its app, as switches grouped by
 * what they do for it (console redesign B2). It replaces the Modules table:
 * slugs, a "depends on" column of slugs, and an on/off/trial dropdown per row
 * with one Apply for the lot.
 *
 * ## One switch, one change — not a batch and an Apply
 *
 * The old table batched because the server validates a request against the
 * state it would *leave behind*: turning on `partner_api` alone is refused,
 * turning on `calendar` and `partner_api` together is not, and a screen that
 * sent one tick at a time could never get there. That reason still holds, and
 * it is now served by the question rather than by the batch: switching on a
 * feature that needs others **offers to turn them all on in one request**
 * ("Website needs Texts and emails — turn on both?"), and switching one off
 * that others need says which go with it and sends them together. So every
 * request is still a whole intention, and there is no pending state to forget
 * — a table of half-changed dropdowns whose Apply was never pressed was the
 * old card's one way to mislead the next operator.
 *
 * Turning on with nothing missing happens at once: it is additive and undone
 * by the same switch. Turning **off** always asks, and the question says what
 * disappears from the business's app (principle 5).
 *
 * ## The server is still the authority
 *
 * The offer is computed from `@yourstore/domain`'s `dependsOn` — the same
 * table `admin-api` checks — but the `409` is still rendered verbatim when it
 * comes (a stale page, a second operator, a newer server), with its hint. And
 * still: turning off hides, it never deletes, and the confirm says so.
 *
 * ## Trial
 *
 * `trial` is a third state the switch cannot show, so it lives in each row's
 * "⋯" menu: turn on as a trial, mark an on feature as a trial, or keep a trial
 * feature (end the trial). A feature on trial shows its switch on and a
 * "Trial" chip, because for everything the business can see it *is* on.
 */
interface Ask {
  title: string;
  consequence: string;
  confirm: string;
  tone: 'primary' | 'danger';
  body: { enable?: string[]; trial?: string[]; disable?: string[] };
  done: string;
}

export function Features({
  tenantId,
  name,
  modules,
  onChanged,
}: {
  tenantId: string;
  /** The business's name, for the sentences that say what changes for it. */
  name: string;
  modules: ModuleRow[];
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const locale = useLocale();
  const toast = useToast();
  const action = useAction();
  const [ask, setAsk] = useState<Ask | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [unfolded, setUnfolded] = useState(false);

  const rows = useMemo(() => new Map(modules.map((m) => [m.module, m])), [modules]);
  const statusOf = (key: string): ModuleStatus => rows.get(key)?.status ?? 'off';
  const enabled = useMemo(
    () => new Set(modules.filter((m) => m.status !== 'off').map((m) => m.module)),
    [modules],
  );
  const foundationOn = FOUNDATION.every((k) => enabled.has(k));

  const since = (row: FeatureRow): string | null => {
    const iso = rows.get(row.keys[0] ?? '')?.enabled_at ?? null;
    if (iso === null || !row.keys.every((k) => enabled.has(k))) return null;
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return null;
    // UTC, as `When` reads every instant on this console; a day is enough here.
    const date = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(
      at,
    );
    return t('setup.features.since', { date });
  };

  /** What the row needs from outside itself, said as names. */
  const needs = (row: FeatureRow): string => {
    const deps = new Set<string>();
    for (const key of row.keys) {
      for (const dep of moduleSpec(key)?.dependsOn ?? []) {
        if (row.keys.includes(dep)) continue;
        // The foundation is "always on"; naming it on every row is noise —
        // unless it is not on, when it is exactly what the row is missing.
        if (FOUNDATION.includes(dep) && foundationOn) continue;
        deps.add(dep);
      }
    }
    return deps.size === 0 ? '' : t('setup.features.needs', { list: namesOf(t, [...deps]) });
  };

  const send = async (body: Ask['body'], message: string): Promise<boolean> => {
    const ok = await action.run(async () => {
      await api.setModules(tenantId, body);
      setDone(message);
      toast(message);
    });
    if (ok) onChanged();
    return ok;
  };

  const begin = (): void => {
    action.clear();
    setDone(null);
  };

  const turnOn = (row: FeatureRow, asTrial: boolean): void => {
    begin();
    const missing = missingFor(enabled, row.keys);
    const own = asTrial ? [...row.keys] : row.keys.filter((k) => !enabled.has(k));
    const body: Ask['body'] = asTrial
      ? { enable: missing, trial: own }
      : { enable: [...missing, ...own] };
    const label = rowName(t, row);
    const all = namesOf(t, [...missing, ...own]);
    const message = asTrial
      ? t('setup.features.done.trial', { list: label }) +
        (missing.length === 0
          ? ''
          : ` ${t('setup.features.done.on', { list: namesOf(t, missing) })}`)
      : t('setup.features.done.on', { list: all });
    if (missing.length === 0) {
      void send(body, message);
      return;
    }
    const needed = namesOf(t, missing);
    const said = { name: label, needs: needed, business: name };
    setAsk({
      title: t('setup.features.on.title', { name: label }),
      consequence: asTrial
        ? t('setup.features.on.trialConsequence', said)
        : t('setup.features.on.consequence', said),
      confirm: t('setup.features.on.confirm', { count: needed.split(', ').length + 1 }),
      tone: 'primary',
      body,
      done: message,
    });
  };

  const keep = (row: FeatureRow): void => {
    begin();
    void send(
      { enable: row.keys.filter((k) => statusOf(k) === 'trial') },
      t('setup.features.done.kept', { list: rowName(t, row) }),
    );
  };

  const turnOff = (row: FeatureRow): void => {
    begin();
    const going = row.keys.filter((k) => enabled.has(k));
    const others = dependentsFor(enabled, going);
    const label = rowName(t, row);
    const list = others.length === 0 ? '' : namesOf(t, others);
    setAsk({
      title: t('setup.features.off.title', { name: label }),
      consequence: [
        t('setup.features.off.gone', { gone: rowGone(t, row), business: name }),
        list === '' ? '' : t('setup.features.off.alsoGoes', { list }),
        t('setup.features.off.keeps'),
      ]
        .filter((s) => s !== '')
        .join(' '),
      confirm: t('setup.features.off.confirm', {
        count: list === '' ? 1 : list.split(', ').length + 1,
      }),
      tone: 'danger',
      body: { disable: [...others, ...going] },
      done: t('setup.features.done.off', { list: list === '' ? label : `${label}, ${list}` }),
    });
  };

  const menuFor = (row: FeatureRow): MenuItem[] => {
    const composite = row.keys.length > 1;
    const state = composite
      ? row.keys.every((k) => enabled.has(k))
        ? 'on'
        : 'off'
      : statusOf(row.keys[0] ?? '');
    const off: MenuItem = {
      key: 'off',
      label: t('setup.features.menu.off'),
      hint: t('setup.features.menu.offHint'),
      tone: 'danger',
      separator: !composite,
      onPick: () => turnOff(row),
    };
    if (composite) return state === 'off' ? [] : [{ ...off, separator: false }];
    if (state === 'off') {
      return [
        {
          key: 'trial',
          label: t('setup.features.menu.trial'),
          hint: t('setup.features.menu.trialHint'),
          onPick: () => turnOn(row, true),
        },
      ];
    }
    if (state === 'trial') {
      return [
        {
          key: 'keep',
          label: t('setup.features.menu.keep'),
          hint: t('setup.features.menu.keepHint'),
          onPick: () => keep(row),
        },
        off,
      ];
    }
    return [
      {
        key: 'to-trial',
        label: t('setup.features.menu.toTrial'),
        hint: t('setup.features.menu.toTrialHint'),
        onPick: () => turnOn(row, true),
      },
      off,
    ];
  };

  const renderRow = (row: FeatureRow): ReactNode => {
    const label = rowName(t, row);
    const on = row.keys.every((k) => enabled.has(k));
    const trial = on && row.keys.some((k) => statusOf(k) === 'trial');
    const locked = row.keys.length > 1 && on;
    const items = menuFor(row);
    const meta = [rowWhat(t, row), needs(row), since(row)].filter(
      (s): s is string => s !== null && s !== '',
    );
    return (
      <div key={row.id} className="feature" data-feature={row.id}>
        <div>
          <div className="feature__name">
            {label}
            {trial ? <Chip tone="accent">{t('setup.features.trial')}</Chip> : null}
          </div>
          {meta.length === 0 ? null : <div className="feature__what">{meta.join(' · ')}</div>}
        </div>
        <div className="feature__controls">
          {locked ? (
            <span className="feature__locked">{t('setup.features.alwaysOn')}</span>
          ) : (
            <Switch
              checked={on}
              label={label}
              disabled={action.busy}
              onChange={(next) => (next ? turnOn(row, false) : turnOff(row))}
            />
          )}
          {items.length === 0 ? null : (
            <Menu
              label="⋯"
              ariaLabel={t('setup.features.more', { name: label })}
              items={items}
              disabled={action.busy}
            />
          )}
        </div>
      </div>
    );
  };

  const shown = (group: FeatureGroup): FeatureRow[] =>
    FEATURE_ROWS.filter(
      (row) =>
        row.group === group &&
        (!FOLDED_GROUPS.has(group) || unfolded || row.keys.some((k) => enabled.has(k))),
    );
  const hiddenCount = FEATURE_ROWS.filter(
    (row) => FOLDED_GROUPS.has(row.group) && !row.keys.some((k) => enabled.has(k)),
  ).length;

  return (
    <Panel
      title={t('setup.features.title')}
      actions={<span className="faint">{t('setup.features.lede', { name })}</span>}
      className="features"
    >
      {ask === null && action.failure !== null ? <Failure failure={action.failure} /> : null}
      {done !== null ? <p className="ok">{done}</p> : null}

      {FEATURE_GROUPS.map((group) => {
        const list = shown(group);
        // The "other kinds" heading stays even when everything under it is
        // folded, because that is where the Show button lives.
        const foldHere = group === 'kinds' && hiddenCount > 0;
        if (list.length === 0 && !foldHere) return null;
        return (
          <div key={group} className="fgroup">
            <h4>{groupTitle(t, group)}</h4>
            {list.map(renderRow)}
            {foldHere ? (
              <div className="feature feature--fold">
                <div className="feature__what">
                  {unfolded ? null : t('setup.features.folded', { count: hiddenCount })}
                </div>
                <button
                  type="button"
                  className="button button--quiet button--sm"
                  aria-expanded={unfolded}
                  onClick={() => setUnfolded((was) => !was)}
                >
                  {unfolded ? t('setup.features.hide') : t('setup.features.show')}
                </button>
              </div>
            ) : null}
          </div>
        );
      })}

      {ask !== null ? (
        <Confirm
          title={ask.title}
          consequence={ask.consequence}
          confirmLabel={ask.confirm}
          tone={ask.tone}
          busy={action.busy}
          failure={action.failure}
          onConfirm={() => {
            void send(ask.body, ask.done).then((ok) => {
              if (ok) setAsk(null);
            });
          }}
          onCancel={() => {
            action.clear();
            setAsk(null);
          }}
        />
      ) : null}
    </Panel>
  );
}

function groupTitle(t: ReturnType<typeof useT>, group: FeatureGroup): string {
  switch (group) {
    case 'day':
      return t('setup.features.group.day');
    case 'selling':
      return t('setup.features.group.selling');
    case 'reaching':
      return t('setup.features.group.reaching');
    case 'kinds':
      return t('setup.features.group.kinds');
    case 'behind':
      return t('setup.features.group.behind');
  }
}
